import { useEffect, useRef } from 'react'
import maplibregl, { type Map as MlMap } from 'maplibre-gl'

/**
 * MapLibre GL JS 5 mount point (Research §Code Examples Pattern 1, lines 244-289).
 *
 * Contract:
 *  - Mounts the `Map` exactly once per component lifetime (StrictMode-safe
 *    via the `mapRef.current` guard — Pitfall 5 lines 745-753).
 *  - `attributionControl: false` + manual `AttributionControl({ compact: true })`
 *    at `bottom-right` satisfies D-66 + UI-SPEC §MapLibre Layer Styling line 466.
 *  - Desktop (≥768px) gets a `NavigationControl`; mobile does not
 *    (UI-SPEC line 467). `matchMedia` is evaluated ONCE at mount — acceptable
 *    per UI-SPEC rationale.
 *  - `onMapReady(map)` fires on `load` so the parent only sees a loaded map.
 *  - Theme swaps never re-init the map; see `theme.ts` `swapTheme()`.
 */
export interface MapContainerProps {
  styleUrl: string
  onMapReady: (map: MlMap) => void
}

export function MapContainer({ styleUrl, onMapReady }: MapContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)

  useEffect(() => {
    if (!containerRef.current) return
    // StrictMode double-invoke protection (Pitfall 5). In production this
    // branch never runs because StrictMode double-mounting is dev-only.
    if (mapRef.current) return

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: styleUrl,
      center: [26.1025, 44.4268], // Bucharest fallback (D-72).
      zoom: 11,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
    })

    map.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      'bottom-right',
    )

    // Desktop-only zoom/compass control (UI-SPEC line 467).
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(min-width: 768px)').matches
    ) {
      map.addControl(
        new maplibregl.NavigationControl({ visualizePitch: false }),
        'bottom-right',
      )
    }

    mapRef.current = map
    map.on('load', () => onMapReady(map))

    return () => {
      map.remove()
      mapRef.current = null
    }
    // We intentionally do NOT react to `styleUrl` changes here. Theme swaps go
    // through `swapTheme(map, nextUrl)` which preserves camera + layers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      ref={containerRef}
      className="map-root"
      style={{ width: '100dvw', height: '100dvh' }}
      aria-label="Map of Romania"
    />
  )
}
